import { PostSort, type PostSummaryDto } from '@gr/core/content/posts/dto';
import { PublicationStatus, type PublicationDto } from '@gr/core/distribution/publishing/dto';
import { addLocalDays, zonedParts, zonedToUtc } from '@gr/core/time/zoned-time';

export enum CalendarView {
  Week = 'week',
  Month = 'month',
}

/** SCH-UC-7: что показывает календарь — публикации (по умолчанию) или посты по дате. */
export enum CalendarSource {
  Publications = 'publications',
  Posts = 'posts',
}

/** Календарный день в поясе блога. */
export interface CalendarDay {
  year: number;
  month: number;
  day: number;
  weekday: number;
}

export function dayKey(d: Pick<CalendarDay, 'year' | 'month' | 'day'>): string {
  return `${d.year}-${String(d.month).padStart(2, '0')}-${String(d.day).padStart(2, '0')}`;
}

/** SCH-UC-7: дни периода; неделя — с понедельника, месяц — целыми неделями (5 или 6). */
export function buildDays(anchor: CalendarDay, view: CalendarView): CalendarDay[] {
  if (view === CalendarView.Week) {
    const monday = addLocalDays(anchor, 1 - anchor.weekday);
    return Array.from({ length: 7 }, (_, i) => addLocalDays(monday, i));
  }
  const first = addLocalDays(anchor, 1 - anchor.day);
  const gridStart = addLocalDays(first, 1 - first.weekday);
  const days = Array.from({ length: 42 }, (_, i) => addLocalDays(gridStart, i));
  return days[35]!.month !== anchor.month ? days.slice(0, 35) : days;
}

/**
 * Опорный день соседнего периода: ±неделя или первое число соседнего месяца; месяц с сегодняшним
 * днём — сегодня, чтобы после перехода на неделю открылась текущая неделя, а не первая в месяце.
 */
export function shiftAnchor(anchor: CalendarDay, view: CalendarView, direction: number, today?: CalendarDay): CalendarDay {
  if (view === CalendarView.Week) return addLocalDays(anchor, 7 * direction);
  const month = anchor.month + direction;
  const year = anchor.year + Math.floor((month - 1) / 12);
  const normalized = ((((month - 1) % 12) + 12) % 12) + 1;
  if (today && today.year === year && today.month === normalized) return today;
  return addLocalDays({ year, month: normalized, day: 1 }, 0);
}

/** SCH-UC-7: подпись периода над календарём — диапазон дней недели («12–18 октября 2026 г.») или месяц. */
export function periodLabel(view: CalendarView, days: readonly CalendarDay[], anchor: CalendarDay, locale: string): string {
  const date = (d: Pick<CalendarDay, 'year' | 'month' | 'day'>): Date => new Date(Date.UTC(d.year, d.month - 1, d.day));
  if (view === CalendarView.Week) {
    return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).formatRange(date(days[0]!), date(days[days.length - 1]!));
  }
  return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(date({ ...anchor, day: 15 }));
}

/** Границы периода в UTC для запроса календаря. */
export function periodRange(days: readonly CalendarDay[], timeZone: string): { from: string; to: string } {
  const first = days[0]!;
  const afterLast = addLocalDays(days[days.length - 1]!, 1);
  return {
    from: zonedToUtc({ ...first, hour: 0, minute: 0 }, timeZone).toISOString(),
    to: zonedToUtc({ ...afterLast, hour: 0, minute: 0 }, timeZone).toISOString(),
  };
}

/** Публикации по дням в поясе блога, в каждом дне — по времени. */
export function groupByDay(publications: readonly PublicationDto[], timeZone: string): Map<string, PublicationDto[]> {
  const map = new Map<string, PublicationDto[]>();
  for (const publication of publications) {
    if (!publication.plannedAt) continue;
    const key = dayKey(zonedParts(new Date(publication.plannedAt), timeZone));
    map.set(key, [...(map.get(key) ?? []), publication]);
  }
  for (const list of map.values()) list.sort((a, b) => (a.plannedAt ?? '').localeCompare(b.plannedAt ?? ''));
  return map;
}

/** Дата поста, по которой он стоит в календаре: создания или изменения. */
export function postDate(post: Pick<PostSummaryDto, 'createdAt' | 'updatedAt'>, by: PostSort): string {
  return by === PostSort.Created ? post.createdAt : post.updatedAt;
}

/** SCH-UC-7: посты по дням пояса блога по дате создания или изменения, в каждом дне — по времени. */
export function groupPostsByDay(posts: readonly PostSummaryDto[], by: PostSort, timeZone: string): Map<string, PostSummaryDto[]> {
  const map = new Map<string, PostSummaryDto[]>();
  for (const post of posts) {
    const key = dayKey(zonedParts(new Date(postDate(post, by)), timeZone));
    map.set(key, [...(map.get(key) ?? []), post]);
  }
  for (const list of map.values()) list.sort((a, b) => postDate(a, by).localeCompare(postDate(b, by)));
  return map;
}

export function conflictKey(publication: Pick<PublicationDto, 'channelId' | 'plannedAt'>): string {
  return `${publication.channelId}@${publication.plannedAt}`;
}

/** SCH-RULE-4: две непрерванные публикации одного канала на одно время. */
export function findConflicts(publications: readonly PublicationDto[]): Set<string> {
  const seen = new Map<string, number>();
  for (const p of publications) {
    if (p.status === PublicationStatus.Cancelled || !p.plannedAt) continue;
    seen.set(conflictKey(p), (seen.get(conflictKey(p)) ?? 0) + 1);
  }
  return new Set([...seen].filter(([, n]) => n > 1).map(([key]) => key));
}
